/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useCustomApplicationsList.ts
 🗓️ Created: 2026-01-16
 🧩 Description:
    Custom hook for managing Custom Applications list with DataGrid integration.
    Follows the Extensions module pattern.
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useDataGrid, type DataGridFetchResult } from '../../../hooks/useDataGrid';
import { listCustomApplications } from '../api/customApplicationsApi';
import type { CustomApplicationColumn } from '../types';

interface UseCustomApplicationsListOptions {
    initialColumns: CustomApplicationColumn[];
    initialSortBy?: string;
    initialSortOrder?: 'asc' | 'desc';
    initialLimit?: number;
    domainId?: number;
}

export function useCustomApplicationsList(options: UseCustomApplicationsListOptions) {
    const { initialColumns, initialSortBy = 'name', initialSortOrder = 'asc', initialLimit = 10, domainId } = options;

    const sortableColumnKeys = useMemo(() => {
        return new Set(initialColumns.filter(column => column.sortable).map(column => column.key));
    }, [initialColumns]);

    const fetchData = useCallback(async (params: {
        page: number;
        limit: number;
        sortBy: string;
        sortOrder: 'asc' | 'desc';
        search?: string;
    }) => {
        const safeSortBy = params.sortBy && sortableColumnKeys.has(params.sortBy)
            ? params.sortBy
            : initialSortBy;
        const safeSortOrder = params.sortOrder === 'desc' ? 'desc' : 'asc';

        const response = await listCustomApplications({
            page: params.page,
            limit: params.limit,
            sortBy: safeSortBy,
            sortOrder: safeSortOrder,
            search: params.search?.trim(),
            domainId,
        });
        return {
            data: response.data || [],
            pagination: response.pagination || {
                page: params.page,
                limit: params.limit,
                total: 0,
                totalPages: 0
            }
        } as DataGridFetchResult;
    }, [sortableColumnKeys, initialSortBy, domainId]);

    const dataGrid = useDataGrid({
        fetchData,
        initialColumns,
        initialSortBy,
        initialSortOrder,
        initialLimit,
    });

    // Refresh data when domainId changes
    useEffect(() => {
        if (domainId !== undefined) {
            dataGrid.refresh();
        }
    }, [domainId]);

    return dataGrid;
}
